import { useState, useEffect } from "react";
import { useStudentNotes } from "@/hooks/useStudentNotes";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, BookOpen, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useUserProfile } from "@/hooks/useUserProfile";
import { getCoursePlayerUrl } from "@/utils/routePaths";

// Define the type for a note with related entities
interface Note {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  lesson_id?: string;
  module_id?: string;
  course_id: string;
  user_id: string;
  courses?: {
    title: string;
  };
  lessons?: {
    title: string;
  };
  modules?: {
    title: string;
  };
}

export const MyNotesTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { userData } = useUserProfile();
  const { data: allNotes, isLoading } = useStudentNotes().getAllNotes(userData?.id);
  
  // Create a map of notes by ID to ensure uniqueness, with proper type casting
  const uniqueNotes = (allNotes || []) as Note[];
  const notesMap = new Map(uniqueNotes.map(note => [note.id, note]));
  const deduplicatedNotes = Array.from(notesMap.values());
  
  // Fix: Remove reference to .data property since allNotes is already the data array
  const hasNotes = deduplicatedNotes.length > 0;
  
  const filteredNotes = deduplicatedNotes.filter(note => {
    const searchLower = searchTerm.toLowerCase();
    
    return (
      note.content?.toLowerCase().includes(searchLower) ||
      note.courses?.title?.toLowerCase().includes(searchLower) ||
      note.lessons?.title?.toLowerCase().includes(searchLower) ||
      note.modules?.title?.toLowerCase().includes(searchLower)
    );
  });
  
  // Debug log when notes change
  useEffect(() => {
    if (allNotes) {
      console.log(`Rendering ${deduplicatedNotes.length} unique notes out of ${(allNotes as Note[])?.length || 0} total notes`);
    }
  }, [allNotes, deduplicatedNotes.length]);
  
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
  };
  
  // Helper function to get note title and link
  const getNoteInfo = (note: Note) => {
    if (note.lesson_id && note.lessons) {
      return {
        title: note.lessons.title,
        link: getCoursePlayerUrl(note.course_id) + `?lessonId=${note.lesson_id}`,
        type: "Lesson"
      };
    } else if (note.module_id && note.modules) {
      return {
        title: note.modules.title,
        link: getCoursePlayerUrl(note.course_id) + `?moduleId=${note.module_id}`,
        type: "Module"
      };
    }
    
    return {
      title: "Unknown",
      link: getCoursePlayerUrl(note.course_id),
      type: "Course"
    };
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>My Notes</CardTitle>
        <CardDescription>
          View and manage all your course notes
        </CardDescription>
        
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search notes..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredNotes && filteredNotes.length > 0 ? (
          <div className="space-y-4">
            {filteredNotes.map((note) => {
              const noteInfo = getNoteInfo(note);
              
              return (
                <Card key={note.id} className="overflow-hidden">
                  <CardHeader className="p-4 pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-1">
                          <BookOpen className="h-3.5 w-3.5" />
                          <span>{note.courses?.title || "Unknown Course"}</span>
                          <span className="mx-1">•</span>
                          <FileText className="h-3.5 w-3.5" />
                          <span>{noteInfo.type}: {noteInfo.title}</span>
                        </div>
                        <CardTitle className="text-base">{note.content.slice(0, 50)}{note.content.length > 50 ? "..." : ""}</CardTitle>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Updated {formatDate(note.updated_at)}
                      </span>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="p-4 pt-0">
                    <div className="line-clamp-2 text-sm text-muted-foreground mb-3">
                      {note.content}
                    </div>
                    
                    <Link to={noteInfo.link}>
                      <Button size="sm" variant="outline">
                        View in Course
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-10">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <FileText className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-lg mb-1">No notes found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? "No notes match your search criteria." : "You haven't taken any notes yet."}
            </p>
            {searchTerm && (
              <Button variant="outline" onClick={() => setSearchTerm("")}>
                Clear Search
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
